/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RuleSetTable.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, IconButton, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import CustomDataGridV2 from "../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
function RuleSetTable({
  rows,
  page,
  rowsPerPage,
  totalItems,
  onPageChange,
  onEdit,
  onViewStandards,
}) {
  const columns = [
    {
      field: "ruleSetName",
      headerName: "Rule Set Name",
      flex: 1,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.2,
    },
    {
      field: "legalStandards",
      headerName: "Legal Standard",
      flex: 1.2,
      sortable: false,
      renderCell: (params) => {
        const list = params.value || [];

        if (!list.length) return <span>-</span>;

        const first = list[0];
        const extra = list.length - 1;

        return (
          <Tooltip
            title="Click to view details"
            arrow
            placement="right"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#000", // pure black tooltip
                  color: "#fff",
                  borderRadius: 0,
                  boxShadow: 3,
                  p: 1,
                  "& .MuiTooltip-arrow": {
                    color: "#000",
                  },
                },
              },
            }}
          >
            <Box
              onClick={() => onViewStandards(list)}
              sx={{
                cursor: "pointer",
                fontWeight: 500,
                color: "#2e7d32", // MUI green[800]
                display: "inline-block",
                borderRadius: "4px",
                px: 0.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  textDecoration: "underline",
                  backgroundColor: "rgba(46, 125, 50, 0.08)",
                },
              }}
            >
              {first.code}
              {extra > 0 && ` (+${extra})`}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "created_by",
      headerName: "Authored By",
      flex: 0.8,
    },
    {
      field: "created_at",
      headerName: "Authored At",
      flex: 0.9,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => onEdit(params.row)}>
          <BorderColorIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ),
    },
  ];

  return (
    <Box
      sx={{
        height: "600px",
        width: "100%",
      }}
    >
      <CustomDataGridV2
        rows={rows}
        columns={columns}
        hideHeader={false}
        isMultiLine={true}
        rowsPerPage={rowsPerPage}
        showPagination={true}
        totalItems={totalItems}
        handlePageChange={onPageChange}
        getRowHeight={() => "auto"}
        page={page}
      />
    </Box>
  );
}

RuleSetTable.propTypes = {
  rows: PropTypes.array,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onEdit: PropTypes.func,
  onViewStandards: PropTypes.func,
};

export default RuleSetTable;
